import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {FileText, ShoppingCart, Package, CreditCard, RotateCcw, AlertCircle} from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const location = useLocation();
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh';

  const content = {
    zh: {
      title: '服務條款 | ONGU Studio',
      description: '使用 ONGU Studio 網站與服務前，請詳閱我們的服務條款與使用規範',
      heading: '服務條款',
      subtitle: 'Terms of Service',
      lastUpdated: '最後更新：2024 年 1 月',
      sections: [
        {
          icon: FileText,
          title: '接受條款',
          content: [
            '歡迎使用 ONGU Studio 網站與服務。使用本網站即表示您同意遵守這些服務條款。',
            '如果您不同意這些條款，請勿使用本網站或購買我們的產品。',
            'ONGU Studio 保留隨時修改這些條款的權利，修改後的條款將在網站上公布。'
          ]
        },
        {
          icon: ShoppingCart,
          title: '購物與訂單',
          content: [
            '所有在本網站上的訂單均需經我們確認後方為有效。我們保留拒絕或取消任何訂單的權利。',
            '產品價格如有變動，恕不另行通知。訂單確認後的價格為準。',
            '產品圖片僅供參考，實際商品可能因拍攝或顯示器設定而略有差異。',
            '部分商品可能因庫存不足而無法供貨，我們將主動通知您並協助處理退款或更換。'
          ]
        },
        {
          icon: CreditCard,
          title: '付款方式',
          content: [
            '我們接受信用卡、ATM 轉帳、超商代碼等多種付款方式。',
            '所有付款資訊均透過加密技術保護，我們不會儲存您的完整信用卡資訊。',
            '訂單成立後，若選擇線上付款，請於 3 天內完成付款，逾期訂單將自動取消。',
            '發票將依照您選擇的方式開立（電子發票或紙本發票）。'
          ]
        },
        {
          icon: Package,
          title: '配送與物流',
          content: [
            '台灣本島訂單通常於 2-5 個工作天內送達，離島地區可能需要額外時間。',
            '訂單滿 NT$1,000 免運費，未滿則酌收運費 NT$100。',
            '國際訂單運費將根據目的地和重量另行計算。',
            '咖啡豆等生鮮商品一經出貨，除產品瑕疵外，恕不接受退換貨。'
          ]
        },
        {
          icon: RotateCcw,
          title: '退換貨政策',
          content: [
            '非生鮮類商品（如器具、禮盒）於收到商品後 7 天內，如有瑕疵或錯誤，可申請退換貨。',
            '退貨商品須保持完整包裝及未使用狀態。',
            '生鮮商品（咖啡豆、濾掛）僅接受品質瑕疵之退換，不接受因個人口味偏好而退貨。',
            '客製化商品、特價商品恕不適用退換貨。',
            '退款將於確認退貨後 7-14 個工作天內退回原付款方式。'
          ]
        },
        {
          icon: AlertCircle,
          title: '智慧財產權',
          content: [
            '本網站所有內容，包括文字、圖片、商標、設計等，均受智慧財產權法保護。',
            '未經 ONGU Studio 書面許可，不得擅自使用、複製、修改或散佈本網站內容。',
            '違反智慧財產權者，我們將依法追究法律責任。'
          ]
        }
      ]
    },
    en: {
      title: 'Terms of Service | ONGU Studio',
      description: 'Please read our terms of service and usage guidelines before using the ONGU Studio website and services',
      heading: 'Terms of Service',
      subtitle: '服務條款',
      lastUpdated: 'Last Updated: January 2024',
      sections: [
        {
          icon: FileText,
          title: 'Acceptance of Terms',
          content: [
            'Welcome to ONGU Studio website and services. By using this website, you agree to abide by these terms of service.',
            'If you do not agree with these terms, please do not use this website or purchase our products.',
            'ONGU Studio reserves the right to modify these terms at any time, and the modified terms will be published on the website.'
          ]
        },
        {
          icon: ShoppingCart,
          title: 'Shopping & Orders',
          content: [
            'All orders placed on this website are valid only after our confirmation. We reserve the right to refuse or cancel any order.',
            'Product prices are subject to change without notice. The confirmed order price shall prevail.',
            'Product images are for reference only. Actual products may vary slightly due to photography or display settings.',
            'Some products may be out of stock. We will proactively notify you and assist with refunds or replacements.'
          ]
        },
        {
          icon: CreditCard,
          title: 'Payment Methods',
          content: [
            'We accept credit cards, ATM transfers, convenience store payment codes, and other payment methods.',
            'All payment information is protected by encryption technology. We do not store your complete credit card information.',
            'After the order is placed, if you choose online payment, please complete payment within 3 days. Overdue orders will be automatically canceled.',
            'Invoices will be issued according to your choice (electronic invoice or paper invoice).'
          ]
        },
        {
          icon: Package,
          title: 'Shipping & Logistics',
          content: [
            'Orders to Taiwan mainland usually arrive within 2-5 business days. Outlying islands may require additional time.',
            'Free shipping for orders over NT$1,000; orders below will be charged NT$100 for shipping.',
            'International order shipping fees will be calculated separately based on destination and weight.',
            'Fresh products such as coffee beans cannot be returned or exchanged once shipped, except for product defects.'
          ]
        },
        {
          icon: RotateCcw,
          title: 'Return & Exchange Policy',
          content: [
            'Non-fresh products (such as equipment, gift boxes) can be returned or exchanged within 7 days of receipt if defective or incorrect.',
            'Returned products must be in complete packaging and unused condition.',
            'Fresh products (coffee beans, drip bags) only accept returns for quality defects, not personal taste preferences.',
            'Customized products and sale items are not eligible for return or exchange.',
            'Refunds will be returned to the original payment method within 7-14 business days after confirming the return.'
          ]
        },
        {
          icon: AlertCircle,
          title: 'Intellectual Property',
          content: [
            'All content on this website, including text, images, trademarks, designs, etc., is protected by intellectual property laws.',
            'Without written permission from ONGU Studio, you may not use, copy, modify, or distribute the content of this website.',
            'Those who violate intellectual property rights will be held legally responsible.'
          ]
        }
      ]
    }
  };

  const t = content[currentLang];

  return (
    <>
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={`https://www.ongucoffee.com/${currentLang}/terms`} />
        <link rel="alternate" hreflang="zh-Hant" href="https://www.ongucoffee.com/zh/terms" />
        <link rel="alternate" hreflang="en" href="https://www.ongucoffee.com/en/terms" />
        <link rel="alternate" hreflang="x-default" href="https://www.ongucoffee.com/zh/terms" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center mb-6">
              <FileText size={40} className="text-gray-400 dark:text-gray-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-wider text-gray-800 dark:text-gray-200 mb-2">
              {t.heading}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-light uppercase tracking-widest mb-6">
              {t.subtitle}
            </p>
            <div className="w-16 h-px bg-gray-400 dark:bg-gray-500 mx-auto mb-6"></div>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t.lastUpdated}
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            {t.sections.map((section, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 transition-all duration-300 hover:shadow-lg"
              >
                {/* Section Header */}
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-700 mr-4">
                    <section.icon size={20} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <h2 className="text-xl font-light tracking-wide text-gray-800 dark:text-gray-200 uppercase">
                    {section.title}
                  </h2>
                </div>

                {/* Section Content */}
                <div className="space-y-4 pl-14">
                  {section.content.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray-600 dark:text-gray-400 leading-relaxed font-light"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-16 p-6 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed font-light text-center">
              {currentLang === 'zh'
                ? '如對本服務條款有任何疑問，請聯繫我們的客服團隊：service@ongucoffee.com 或 +886-2-2345-6789'
                : 'If you have any questions about these terms of service, please contact our customer service team: service@ongucoffee.com or +886-2-2345-6789'}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfServicePage;